import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import colorTheme from "../components/common/color";

// Type definitions
type AppointmentStatus =
  | "confirmed"
  | "arrived"
  | "completed"
  | "scheduled"
  | "cancelled"
  | "no_show";
type AlertType = "warning" | "error" | "success" | "info";

interface DashboardStats {
  todayAppointments: number;
  pendingAppointments: number;
  completedToday: number;
  todayRevenue: number;
  weeklyRevenue: number;
  monthlyProgress: number;
  activePatients: number;
  equipmentIssues: number;
}

interface TodayAppointment {
  id: number;
  time: string;
  formatted_time: string;
  patient: {
    id: number;
    first_name: string;
    last_name: string;
    full_name: string;
  };
  doctor: {
    id: number;
    first_name: string;
    last_name: string;
    full_name: string;
  };
  status: AppointmentStatus;
  type: string;
  duration: number;
}

interface SystemAlert {
  id: number;
  type: AlertType;
  message: string;
  priority: "high" | "medium" | "low";
  created_at: string;
  equipment_id?: number;
  appointment_id?: number;
}

interface RecentActivity {
  id: number;
  type: "appointment" | "revenue" | "patient" | "equipment" | "service";
  message: string;
  time: string;
  icon: string;
  created_at: string;
  user_name?: string;
  amount?: number;
}

const PerfectDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Add user role state
  const [userRole, setUserRole] = useState<string>("");
  const [currentUser, setCurrentUser] = useState<any>(null);

  // Real data state
  const [stats, setStats] = useState<DashboardStats>({
    todayAppointments: 0,
    pendingAppointments: 0,
    completedToday: 0,
    todayRevenue: 0,
    weeklyRevenue: 0,
    monthlyProgress: 0,
    activePatients: 0,
    equipmentIssues: 0,
  });

  const [todayAppointments, setTodayAppointments] = useState<
    TodayAppointment[]
  >([]);
  const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([]);
  const [alerts, setAlerts] = useState<SystemAlert[]>([]);

  // Navigation helper function - now uses React Router
  const handleNavigation = (page: string) => {
    navigate(`/${page}`);
  };
  const getResponsiveStyles = () => {
    return {
      // Container styles for different screen sizes
      container: {
        padding: "24px",
        background: colorTheme.gray50,
        minHeight: "100vh",
        fontFamily: "system-ui, -apple-system, sans-serif",
      },

      // Main wrapper with responsive max-width
      mainWrapper: {
        maxWidth: "1400px",
        margin: "0 auto",
      },

      // Header responsive styles
      header: {
        background: `linear-gradient(135deg, ${colorTheme.primary} 0%, #1e40af 100%)`,
        borderRadius: "16px",
        padding: "32px",
        marginBottom: "24px",
        color: "white",
        position: "relative" as const,
        overflow: "hidden",
      },

      // Metrics grid responsive
      metricsGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
        gap: "20px",
        marginBottom: "24px",
      },

      // Main content grid responsive
      mainContentGrid: {
        display: "grid",
        gridTemplateColumns: "2fr 1fr",
        gap: "24px",
        marginBottom: "24px",
      },

      // Quick actions responsive
      quickActionsGrid: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: "12px",
      },

      // Bottom section responsive
      bottomGrid: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: "24px",
      },

      // Performance grid responsive
      performanceGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
        gap: "20px",
      },
    };
  };

  const responsiveStyles = getResponsiveStyles();
  // Add fetchCurrentUser function (same as in RevenueDashboard)
  const fetchCurrentUser = async (): Promise<void> => {
    try {
      const token = localStorage.getItem("auth_token");
      if (!token) {
        setUserRole("");
        return;
      }

      const response = await fetch(
        `${
          process.env.REACT_APP_API_URL ||
          "https://dr-bassam-elayoubi.caduceuslb.com/api"
        }/user`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.ok) {
        const userData = await response.json();
        setCurrentUser(userData);

        // Handle roles array - get the primary role or check for specific roles
        const roles = userData.roles || [];
        if (roles.includes("admin")) {
          setUserRole("admin");
        } else if (roles.includes("doctor")) {
          setUserRole("doctor");
        } else if (roles.includes("secretary")) {
          setUserRole("secretary");
        } else {
          setUserRole(roles[0] || ""); // Use first role as fallback
        }
      } else {
        setUserRole("");
      }
    } catch (error) {
      console.error("Failed to fetch user data:", error);
      setUserRole("");
    }
  };

  // Check if user can see revenue data
  const canSeeRevenue = () => {
    return userRole && !userRole.includes("secretary");
  };

  // Live clock
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Fetch user role on component mount
  useEffect(() => {
    fetchCurrentUser();
  }, []);

  // Load dashboard data from database
  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);

        const today = new Date().toISOString().split("T")[0];
        const weekStart = new Date();
        weekStart.setDate(weekStart.getDate() - 7);
        const weekStartStr = weekStart.toISOString().split("T")[0];

        // Base requests that everyone can see
        const baseRequests = [
          // Get today's appointments
          fetch(`${process.env.REACT_APP_API_URL}/appointments?date=${today}`, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
              Accept: "application/json",
            },
          }),
          // Get patient statistics
          fetch(`${process.env.REACT_APP_API_URL}/patients-statistics`, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
              Accept: "application/json",
            },
          }),
          // Get system alerts
          fetch(
            `${process.env.REACT_APP_API_URL}/system-alerts?active=true&limit=5`,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
                Accept: "application/json",
              },
            }
          ),
          // Get recent activity
          fetch(`${process.env.REACT_APP_API_URL}/recent-activity?limit=10`, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
              Accept: "application/json",
            },
          }),
        ];

        // Revenue requests - only for non-secretary users
        const revenueRequests = canSeeRevenue()
          ? [
              // Get today's revenue
              fetch(
                `${process.env.REACT_APP_API_URL}/revenue/summary?start_date=${today}&end_date=${today}`,
                {
                  headers: {
                    Authorization: `Bearer ${localStorage.getItem(
                      "auth_token"
                    )}`,
                    Accept: "application/json",
                  },
                }
              ),
              // Get weekly revenue
              fetch(
                `${process.env.REACT_APP_API_URL}/revenue/summary?start_date=${weekStartStr}&end_date=${today}`,
                {
                  headers: {
                    Authorization: `Bearer ${localStorage.getItem(
                      "auth_token"
                    )}`,
                    Accept: "application/json",
                  },
                }
              ),
            ]
          : [];

        // Parallel data fetching for better performance
        const allRequests = [...baseRequests, ...revenueRequests];
        const responses = await Promise.all(allRequests);

        // Process base responses (always available)
        const todayAppts = await responses[0].json();
        const patientsStats = await responses[1].json();
        const systemAlerts = await responses[2].json();
        const activity = await responses[3].json();

        // Process revenue responses (only if user has access)
        let todayRevenue = { total_revenue: 0 };
        let weeklyRevenue = { total_revenue: 0 };

        if (canSeeRevenue() && responses.length > 4) {
          todayRevenue = await responses[4].json();
          if (responses.length > 5) {
            weeklyRevenue = await responses[5].json();
          }
        }

        // Update appointments
        setTodayAppointments(todayAppts.data || []);

        // Calculate appointment stats
        const appointments = todayAppts.data || [];
        const pendingCount = appointments.filter(
          (apt: any) => apt.status === "scheduled" || apt.status === "confirmed"
        ).length;
        const completedCount = appointments.filter(
          (apt: any) => apt.status === "completed"
        ).length;

        // Update stats
        setStats({
          todayAppointments: appointments.length,
          pendingAppointments: pendingCount,
          completedToday: completedCount,
          todayRevenue: canSeeRevenue() ? todayRevenue.total_revenue || 0 : 0,
          weeklyRevenue: canSeeRevenue() ? weeklyRevenue.total_revenue || 0 : 0,
          monthlyProgress: canSeeRevenue()
            ? calculateMonthlyProgress(weeklyRevenue.total_revenue || 0)
            : 0,
          activePatients: patientsStats.data?.active_patients || 0,
          equipmentIssues:
            systemAlerts.data?.filter(
              (alert: any) =>
                alert.type === "warning" && alert.message.includes("equipment")
            ).length || 0,
        });

        // Update alerts and activity
        setAlerts(systemAlerts.data || []);
        setRecentActivity(formatRecentActivity(activity.data || []));
      } catch (err: any) {
        console.error("Error loading dashboard data:", err);
        setError(err.message || "Failed to load dashboard data");
      } finally {
        setLoading(false);
      }
    };

    // Only load data if we have user role information
    if (userRole) {
      loadDashboardData();
    }

    // Refresh data every 2 minutes for real-time updates
    const refreshInterval = setInterval(() => {
      if (userRole) {
        loadDashboardData();
      }
    }, 2 * 60 * 1000);
    return () => clearInterval(refreshInterval);
  }, [userRole]); // Add userRole as dependency

  // Helper functions
  const calculateMonthlyProgress = (weeklyRevenue: number): number => {
    const monthlyTarget = 50000;
    const estimatedMonthly = weeklyRevenue * 4.33;
    return Math.min(Math.round((estimatedMonthly / monthlyTarget) * 100), 100);
  };

  const formatRecentActivity = (activities: any[]): RecentActivity[] => {
    return activities.map((activity: any) => ({
      ...activity,
      icon: getActivityIcon(activity.type),
      time: formatTimeAgo(activity.created_at),
    }));
  };

  const getActivityIcon = (type: string): string => {
    switch (type) {
      case "appointment":
        return "📅";
      case "revenue":
        return "💰";
      case "patient":
        return "👤";
      case "equipment":
        return "🔧";
      case "service":
        return "⚕️";
      default:
        return "📋";
    }
  };

  const formatTimeAgo = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor(
      (now.getTime() - date.getTime()) / (1000 * 60)
    );

    if (diffInMinutes < 1) return "Just now";
    if (diffInMinutes < 60) return `${diffInMinutes} mins ago`;
    if (diffInMinutes < 1440)
      return `${Math.floor(diffInMinutes / 60)} hours ago`;
    return `${Math.floor(diffInMinutes / 1440)} days ago`;
  };

  const getStatusColor = (status: AppointmentStatus): string => {
    switch (status) {
      case "confirmed":
        return colorTheme.success;
      case "arrived":
        return colorTheme.info;
      case "completed":
        return colorTheme.gray500;
      case "scheduled":
        return colorTheme.warning;
      case "cancelled":
        return colorTheme.error;
      case "no_show":
        return colorTheme.warning;
      default:
        return colorTheme.gray500;
    }
  };

  const getAlertColor = (type: AlertType): string => {
    switch (type) {
      case "warning":
        return colorTheme.warning;
      case "error":
        return colorTheme.error;
      case "success":
        return colorTheme.success;
      case "info":
        return colorTheme.info;
      default:
        return colorTheme.gray500;
    }
  };

  // Modified quick actions to exclude revenue for secretaries
  const quickActions: Array<{
    id: string;
    title: string;
    icon: string;
    color: string;
    path: string;
  }> = [
    {
      id: "new-appointment",
      title: "New Appointment",
      icon: "📅",
      color: colorTheme.primary,
      path: "appointments",
    },
    {
      id: "add-patient",
      title: "Add Patient",
      icon: "👤",
      color: colorTheme.success,
      path: "patients",
    },
    // Only show revenue report for non-secretary users
    ...(canSeeRevenue()
      ? [
          {
            id: "revenue-report",
            title: "Revenue Report",
            icon: "💰",
            color: colorTheme.warning,
            path: "revenue",
          },
        ]
      : []),
    {
      id: "manage-services",
      title: "Services",
      icon: "⚕️",
      color: colorTheme.info,
      path: "services",
    },
  ];

  // Loading state
  if (loading) {
    return (
      <div
        style={{
          padding: "24px",
          background: colorTheme.gray50,
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            background: colorTheme.cardBg,
            borderRadius: "16px",
            padding: "32px",
            textAlign: "center",
            boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
          }}
        >
          <div
            style={{
              width: "40px",
              height: "40px",
              border: `4px solid ${colorTheme.gray200}`,
              borderTop: `4px solid ${colorTheme.primary}`,
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
              margin: "0 auto 16px auto",
            }}
          />
          <p style={{ color: colorTheme.gray600 }}>Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div
        style={{
          padding: "24px",
          background: colorTheme.gray50,
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            background: colorTheme.cardBg,
            borderRadius: "16px",
            padding: "32px",
            textAlign: "center",
            boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
            maxWidth: "400px",
          }}
        >
          <div style={{ fontSize: "48px", marginBottom: "16px" }}>⚠️</div>
          <h3 style={{ color: colorTheme.error, marginBottom: "16px" }}>
            Failed to Load Dashboard
          </h3>
          <p style={{ color: colorTheme.gray600, marginBottom: "24px" }}>
            {error}
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: "12px 24px",
              background: colorTheme.primary,
              color: "white",
              border: "none",
              borderRadius: "8px",
              cursor: "pointer",
            }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Filter metrics based on user role
  const getMetricsForRole = () => {
    const baseMetrics = [
      {
        title: "Today's Appointments",
        value: stats.todayAppointments,
        displayValue: stats.todayAppointments.toString(),
        icon: "📅",
        color: colorTheme.primary,
        change:
          stats.todayAppointments > 0 ? "+" + stats.todayAppointments : "0",
        subtitle: `${stats.completedToday} completed`,
        onClick: () => handleNavigation("appointments"),
      },
      {
        title: "Pending Confirmations",
        value: stats.pendingAppointments,
        displayValue: stats.pendingAppointments.toString(),
        icon: "⏳",
        color: colorTheme.warning,
        change: stats.pendingAppointments > 0 ? "Action needed" : "All clear",
        subtitle: "Need attention",
        onClick: () => handleNavigation("appointments"),
      },
      {
        title: "Completed Today",
        value: stats.completedToday,
        displayValue: stats.completedToday.toString(),
        icon: "✅",
        color: colorTheme.success,
        change: "+" + stats.completedToday,
        subtitle: "Services delivered",
        onClick: () => handleNavigation("appointments"),
      },
    ];

    // Add revenue metric only for non-secretary users
    if (canSeeRevenue()) {
      baseMetrics.push({
        title: "Today's Revenue",
        value: stats.todayRevenue,
        displayValue: `${stats.todayRevenue.toLocaleString()}`,
        icon: "💰",
        color: colorTheme.info,
        change: stats.todayRevenue > 0 ? "Active" : "Pending",
        subtitle: "Financial performance",
        onClick: () => handleNavigation("revenue"),
      });
    }

    return baseMetrics;
  };

  return (
    <div style={responsiveStyles.container}>
      <div style={responsiveStyles.mainWrapper}>
        {/* Header Section - with responsive classes */}
        <div style={responsiveStyles.header} className="dashboard-header">
          <div
            style={{
              position: "absolute",
              top: "-50px",
              right: "-50px",
              width: "200px",
              height: "200px",
              background: "rgba(255,255,255,0.1)",
              borderRadius: "50%",
            }}
          ></div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              position: "relative",
              zIndex: 1,
            }}
            className="header-content"
          >
            <div>
              <h1
                style={{
                  fontSize: "32px",
                  fontWeight: "700",
                  margin: "0 0 8px 0",
                }}
                className="dashboard-title"
              >
                Good{" "}
                {currentTime.getHours() < 12
                  ? "Morning"
                  : currentTime.getHours() < 18
                  ? "Afternoon"
                  : "Evening"}
                !
              </h1>
              <p
                style={{ fontSize: "18px", opacity: 0.9, margin: 0 }}
                className="dashboard-subtitle"
              >
                Welcome back to Caduceus -{" "}
                {currentTime.toLocaleDateString("en-US", {
                  weekday: "long",
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </p>
              {stats.todayAppointments > 0 && (
                <p
                  style={{
                    fontSize: "14px",
                    opacity: 0.8,
                    margin: "8px 0 0 0",
                  }}
                  className="dashboard-appointments-count"
                >
                  You have {stats.todayAppointments} appointments today
                </p>
              )}
              <p
                style={{
                  fontSize: "12px",
                  opacity: 0.7,
                  margin: "4px 0 0 0",
                }}
                className="dashboard-role"
              >
                {userRole &&
                  `Role: ${
                    userRole.charAt(0).toUpperCase() + userRole.slice(1)
                  }`}
              </p>
            </div>
            <div style={{ textAlign: "right" }} className="header-time">
              <div
                style={{ fontSize: "24px", fontWeight: "600" }}
                className="current-time"
              >
                {currentTime.toLocaleTimeString("en-US", {
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: true,
                })}
              </div>
              <div
                style={{ opacity: 0.8, fontSize: "14px" }}
                className="time-label"
              >
                Live Time
              </div>
              <div
                style={{
                  background: "rgba(255,255,255,0.2)",
                  borderRadius: "12px",
                  padding: "8px 12px",
                  marginTop: "8px",
                  fontSize: "12px",
                }}
                className="system-status"
              >
                System Online
              </div>
            </div>
          </div>
        </div>

        {/* Key Metrics Row - with responsive grid */}
        <div style={responsiveStyles.metricsGrid} className="metrics-grid">
          {getMetricsForRole().map((metric, index) => (
            <div
              key={index}
              style={{
                background: colorTheme.cardBg,
                borderRadius: "12px",
                padding: "24px",
                boxShadow: "0 4px 6px rgba(0, 0, 0, 0.07)",
                border: "1px solid #f1f5f9",
                transition: "all 0.3s ease",
                cursor: "pointer",
              }}
              className="metric-card"
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow =
                  "0 8px 25px rgba(0, 0, 0, 0.1)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow =
                  "0 4px 6px rgba(0, 0, 0, 0.07)";
              }}
              onClick={metric.onClick}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  marginBottom: "12px",
                }}
              >
                <div style={{ fontSize: "32px" }}>{metric.icon}</div>
                <span
                  style={{
                    background: `${metric.color}15`,
                    color: metric.color,
                    padding: "4px 8px",
                    borderRadius: "12px",
                    fontSize: "12px",
                    fontWeight: "600",
                  }}
                  className="metric-change"
                >
                  {metric.change}
                </span>
              </div>
              <div
                style={{
                  fontSize: "28px",
                  fontWeight: "700",
                  color: colorTheme.gray900,
                  marginBottom: "4px",
                }}
                className="metric-value"
              >
                {metric.displayValue || metric.value}
              </div>
              <div
                style={{
                  fontSize: "14px",
                  color: colorTheme.gray500,
                  marginBottom: "2px",
                }}
                className="metric-title"
              >
                {metric.title}
              </div>
              <div
                style={{ fontSize: "12px", color: colorTheme.gray500 }}
                className="metric-subtitle"
              >
                {metric.subtitle}
              </div>
            </div>
          ))}
        </div>

        {/* Main Content Grid - responsive */}
        <div
          style={responsiveStyles.mainContentGrid}
          className="main-content-grid"
        >
          {/* Today's Schedule */}
          <div
            style={{
              background: colorTheme.cardBg,
              borderRadius: "16px",
              padding: "24px",
              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.07)",
            }}
            className="schedule-container"
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "20px",
              }}
            >
              <h3
                style={{
                  fontSize: "20px",
                  fontWeight: "600",
                  color: colorTheme.gray900,
                  margin: 0,
                }}
                className="schedule-title"
              >
                Today's Schedule ({todayAppointments.length})
              </h3>
              <button
                onClick={() => handleNavigation("appointments")}
                style={{
                  padding: "8px 16px",
                  background: colorTheme.primary,
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: "14px",
                  cursor: "pointer",
                }}
                className="view-all-btn"
              >
                View All
              </button>
            </div>

            {todayAppointments.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "40px",
                  color: colorTheme.gray500,
                }}
                className="no-appointments"
              >
                <div style={{ fontSize: "48px", marginBottom: "16px" }}>📅</div>
                <p>No appointments scheduled for today</p>
                <button
                  onClick={() => handleNavigation("appointments")}
                  style={{
                    padding: "8px 16px",
                    background: colorTheme.success,
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    fontSize: "14px",
                    cursor: "pointer",
                    marginTop: "12px",
                  }}
                >
                  Schedule Appointment
                </button>
              </div>
            ) : (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                }}
                className="appointments-list"
              >
                {todayAppointments.slice(0, 6).map((apt) => (
                  <div
                    key={apt.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      padding: "16px",
                      background: colorTheme.gray50,
                      borderRadius: "12px",
                      border: `2px solid ${getStatusColor(apt.status)}15`,
                      cursor: "pointer",
                    }}
                    className="schedule-item"
                    onClick={() => handleNavigation("appointments")}
                  >
                    <div
                      style={{
                        width: "8px",
                        height: "40px",
                        background: getStatusColor(apt.status),
                        borderRadius: "4px",
                        marginRight: "16px",
                      }}
                    ></div>

                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                        }}
                      >
                        <div>
                          <div
                            style={{
                              fontSize: "16px",
                              fontWeight: "600",
                              color: colorTheme.gray900,
                            }}
                            className="patient-name"
                          >
                            {apt.patient.full_name ||
                              `${apt.patient.first_name} ${apt.patient.last_name}`}
                          </div>
                          <div
                            style={{
                              fontSize: "14px",
                              color: colorTheme.gray500,
                            }}
                            className="appointment-details"
                          >
                            {apt.doctor.full_name ||
                              `${apt.doctor.first_name} ${apt.doctor.last_name}`}{" "}
                            • {apt.type} • {apt.duration}min
                          </div>
                        </div>
                        <div style={{ textAlign: "right" }}>
                          <div
                            style={{
                              fontSize: "14px",
                              fontWeight: "600",
                              color: colorTheme.gray900,
                            }}
                            className="appointment-time"
                          >
                            {apt.formatted_time || apt.time}
                          </div>
                          <span
                            style={{
                              padding: "4px 8px",
                              background: `${getStatusColor(apt.status)}15`,
                              color: getStatusColor(apt.status),
                              borderRadius: "12px",
                              fontSize: "12px",
                              fontWeight: "500",
                              textTransform: "capitalize",
                            }}
                            className="appointment-status"
                          >
                            {apt.status.replace("_", " ")}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                {todayAppointments.length > 6 && (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "12px",
                      color: colorTheme.gray500,
                      fontSize: "14px",
                    }}
                    className="more-appointments"
                  >
                    +{todayAppointments.length - 6} more appointments
                    <button
                      onClick={() => handleNavigation("appointments")}
                      style={{
                        marginLeft: "8px",
                        padding: "4px 8px",
                        background: colorTheme.primary,
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        fontSize: "12px",
                        cursor: "pointer",
                      }}
                    >
                      View All
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Quick Actions & Alerts - Sidebar */}
          <div
            style={{ display: "flex", flexDirection: "column", gap: "20px" }}
            className="sidebar-container"
          >
            {/* Quick Actions */}
            <div
              style={{
                background: colorTheme.cardBg,
                borderRadius: "16px",
                padding: "20px",
                boxShadow: "0 4px 6px rgba(0, 0, 0, 0.07)",
              }}
              className="quick-actions-container"
            >
              <h3
                style={{
                  fontSize: "18px",
                  fontWeight: "600",
                  color: colorTheme.gray900,
                  margin: "0 0 16px 0",
                }}
                className="quick-actions-title"
              >
                Quick Actions
              </h3>
              <div
                style={responsiveStyles.quickActionsGrid}
                className="quick-actions-grid"
              >
                {quickActions.map((action) => (
                  <button
                    key={action.id}
                    onClick={() => handleNavigation(action.path)}
                    style={{
                      padding: "16px 12px",
                      background: `${action.color}10`,
                      border: `2px solid ${action.color}20`,
                      borderRadius: "12px",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      textAlign: "center",
                    }}
                    className="quick-action-btn"
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = `${action.color}20`;
                      e.currentTarget.style.transform = "translateY(-2px)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = `${action.color}10`;
                      e.currentTarget.style.transform = "translateY(0)";
                    }}
                  >
                    <div
                      style={{ fontSize: "24px", marginBottom: "8px" }}
                      className="quick-action-icon"
                    >
                      {action.icon}
                    </div>
                    <div
                      style={{
                        fontSize: "12px",
                        fontWeight: "600",
                        color: colorTheme.gray900,
                      }}
                      className="quick-action-title"
                    >
                      {action.title}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* System Alerts */}
            <div
              style={{
                background: colorTheme.cardBg,
                borderRadius: "16px",
                padding: "20px",
                boxShadow: "0 4px 6px rgba(0, 0, 0, 0.07)",
              }}
              className="system-alerts-container"
            >
              <h3
                style={{
                  fontSize: "18px",
                  fontWeight: "600",
                  color: colorTheme.gray900,
                  margin: "0 0 16px 0",
                }}
                className="alerts-title"
              >
                System Alerts ({alerts.length})
              </h3>

              {alerts.length === 0 ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: "20px",
                    color: colorTheme.gray500,
                  }}
                  className="no-alerts"
                >
                  <div style={{ fontSize: "32px", marginBottom: "8px" }}>
                    ✅
                  </div>
                  <div style={{ fontSize: "14px" }}>
                    All systems running smoothly
                  </div>
                </div>
              ) : (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "12px",
                  }}
                  className="alerts-list"
                >
                  {alerts.slice(0, 3).map((alert) => (
                    <div
                      key={alert.id}
                      style={{
                        padding: "12px",
                        background: `${getAlertColor(alert.type)}10`,
                        border: `1px solid ${getAlertColor(alert.type)}30`,
                        borderRadius: "8px",
                        borderLeft: `4px solid ${getAlertColor(alert.type)}`,
                      }}
                      className="system-alert"
                    >
                      <div
                        style={{
                          fontSize: "14px",
                          color: colorTheme.gray900,
                          fontWeight: "500",
                        }}
                        className="alert-message"
                      >
                        {alert.message}
                      </div>
                      <div
                        style={{
                          fontSize: "12px",
                          color: colorTheme.gray500,
                          marginTop: "4px",
                        }}
                        className="alert-timestamp"
                      >
                        Priority: {alert.priority} •{" "}
                        {formatTimeAgo(alert.created_at)}
                      </div>
                    </div>
                  ))}

                  {alerts.length > 3 && (
                    <div
                      style={{
                        textAlign: "center",
                        padding: "8px",
                        color: colorTheme.gray500,
                        fontSize: "12px",
                      }}
                      className="more-alerts"
                    >
                      +{alerts.length - 3} more alerts
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Bottom Section - responsive */}
        <div style={responsiveStyles.bottomGrid} className="bottom-section">
          {/* Recent Activity */}
          <div
            style={{
              background: colorTheme.cardBg,
              borderRadius: "16px",
              padding: "24px",
              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.07)",
            }}
            className="recent-activity-container"
          >
            <h3
              style={{
                fontSize: "20px",
                fontWeight: "600",
                color: colorTheme.gray900,
                margin: "0 0 20px 0",
              }}
              className="activity-title"
            >
              Recent Activity ({recentActivity.length})
            </h3>

            {recentActivity.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "40px",
                  color: colorTheme.gray500,
                }}
                className="no-activity"
              >
                <div style={{ fontSize: "48px", marginBottom: "16px" }}>📋</div>
                <p>No recent activity</p>
              </div>
            ) : (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                }}
                className="activity-list"
              >
                {recentActivity.slice(0, 5).map((activity) => (
                  <div
                    key={activity.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      padding: "12px 0",
                      borderBottom: `1px solid ${colorTheme.gray100}`,
                    }}
                    className="activity-item"
                  >
                    <div
                      style={{
                        width: "40px",
                        height: "40px",
                        borderRadius: "50%",
                        background: colorTheme.gray100,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "16px",
                      }}
                    >
                      {activity.icon}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          fontSize: "14px",
                          fontWeight: "500",
                          color: colorTheme.gray900,
                        }}
                        className="activity-message"
                      >
                        {activity.message}
                        {activity.amount && canSeeRevenue() && (
                          <span
                            style={{
                              color: colorTheme.success,
                              fontWeight: "600",
                              marginLeft: "4px",
                            }}
                          >
                            ${activity.amount}
                          </span>
                        )}
                      </div>
                      <div
                        style={{ fontSize: "12px", color: colorTheme.gray500 }}
                        className="activity-time"
                      >
                        {activity.time}
                        {activity.user_name && ` • by ${activity.user_name}`}
                      </div>
                    </div>
                  </div>
                ))}

                {recentActivity.length > 5 && (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "12px",
                      color: colorTheme.gray500,
                      fontSize: "12px",
                    }}
                    className="more-activity"
                  >
                    +{recentActivity.length - 5} more activities
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Revenue Overview - Only show for non-secretary users */}
          {canSeeRevenue() && (
            <div
              style={{
                background: colorTheme.cardBg,
                borderRadius: "16px",
                padding: "24px",
                boxShadow: "0 4px 6px rgba(0, 0, 0, 0.07)",
              }}
              className="revenue-overview-container"
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "20px",
                }}
              >
                <h3
                  style={{
                    fontSize: "20px",
                    fontWeight: "600",
                    color: colorTheme.gray900,
                    margin: 0,
                  }}
                  className="revenue-title"
                >
                  Revenue Overview
                </h3>
                <button
                  onClick={() => handleNavigation("revenue")}
                  style={{
                    padding: "8px 16px",
                    background: colorTheme.success,
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    fontSize: "14px",
                    cursor: "pointer",
                  }}
                  className="revenue-report-btn"
                >
                  Full Report
                </button>
              </div>

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                }}
                className="revenue-cards"
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "16px",
                    background: `${colorTheme.success}10`,
                    borderRadius: "12px",
                  }}
                  className="revenue-card today-revenue"
                >
                  <div>
                    <div
                      style={{ fontSize: "14px", color: colorTheme.gray600 }}
                    >
                      Today's Revenue
                    </div>
                    <div
                      style={{
                        fontSize: "24px",
                        fontWeight: "700",
                        color: colorTheme.success,
                      }}
                      className="revenue-value"
                    >
                      ${stats.todayRevenue.toLocaleString()}
                    </div>
                    <div
                      style={{
                        fontSize: "12px",
                        color: colorTheme.gray500,
                        marginTop: "2px",
                      }}
                      className="revenue-subtitle"
                    >
                      From {stats.completedToday} completed services
                    </div>
                  </div>
                  <div style={{ fontSize: "32px" }}>📈</div>
                </div>

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "16px",
                    background: `${colorTheme.info}10`,
                    borderRadius: "12px",
                  }}
                  className="revenue-card weekly-revenue"
                >
                  <div>
                    <div
                      style={{ fontSize: "14px", color: colorTheme.gray600 }}
                    >
                      This Week
                    </div>
                    <div
                      style={{
                        fontSize: "24px",
                        fontWeight: "700",
                        color: colorTheme.info,
                      }}
                      className="revenue-value"
                    >
                      ${stats.weeklyRevenue.toLocaleString()}
                    </div>
                    <div
                      style={{
                        fontSize: "12px",
                        color: colorTheme.gray500,
                        marginTop: "2px",
                      }}
                      className="revenue-subtitle"
                    >
                      {stats.weeklyRevenue > stats.todayRevenue * 7
                        ? "Above"
                        : "Below"}{" "}
                      daily average
                    </div>
                  </div>
                  <div style={{ fontSize: "32px" }}>💎</div>
                </div>

                <div
                  style={{
                    padding: "16px",
                    background: colorTheme.gray50,
                    borderRadius: "12px",
                    textAlign: "center",
                  }}
                  className="monthly-progress"
                >
                  <div
                    style={{
                      fontSize: "14px",
                      color: colorTheme.gray600,
                      marginBottom: "8px",
                    }}
                  >
                    Monthly Target Progress
                  </div>
                  <div
                    style={{
                      width: "100%",
                      height: "8px",
                      background: colorTheme.gray200,
                      borderRadius: "4px",
                      overflow: "hidden",
                      marginBottom: "8px",
                    }}
                  >
                    <div
                      style={{
                        width: `${Math.min(stats.monthlyProgress, 100)}%`,
                        height: "100%",
                        background: `linear-gradient(90deg, ${colorTheme.success} 0%, #059669 100%)`,
                        borderRadius: "4px",
                        transition: "width 0.3s ease",
                      }}
                    ></div>
                  </div>
                  <div style={{ fontSize: "12px", color: colorTheme.gray500 }}>
                    {stats.monthlyProgress}% of $50,000 target
                  </div>
                  <div
                    style={{
                      fontSize: "12px",
                      color: colorTheme.gray100,
                      marginTop: "4px",
                    }}
                  >
                    Estimated: $
                    {Math.round(stats.weeklyRevenue * 4.33).toLocaleString()}{" "}
                    this month
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Performance Summary Footer */}
        <div
          style={{
            background: colorTheme.cardBg,
            borderRadius: "16px",
            padding: "24px",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.07)",
            marginTop: "24px",
          }}
          className="performance-summary"
        >
          <h3
            style={{
              fontSize: "18px",
              fontWeight: "600",
              color: colorTheme.gray900,
              margin: "0 0 16px 0",
            }}
            className="performance-title"
          >
            Today's Performance Summary
          </h3>
          <div
            style={responsiveStyles.performanceGrid}
            className="performance-grid"
          >
            <div style={{ textAlign: "center" }} className="performance-metric">
              <div
                style={{
                  fontSize: "20px",
                  fontWeight: "700",
                  color: colorTheme.primary,
                }}
                className="performance-value"
              >
                {(
                  (stats.completedToday /
                    Math.max(stats.todayAppointments, 1)) *
                  100
                ).toFixed(0)}
                %
              </div>
              <div
                style={{ fontSize: "12px", color: colorTheme.gray500 }}
                className="performance-label"
              >
                Completion Rate
              </div>
            </div>

            {canSeeRevenue() && (
              <div
                style={{ textAlign: "center" }}
                className="performance-metric"
              >
                <div
                  style={{
                    fontSize: "20px",
                    fontWeight: "700",
                    color: colorTheme.success,
                  }}
                  className="performance-value"
                >
                  $
                  {stats.todayRevenue > 0
                    ? Math.round(
                        stats.todayRevenue / Math.max(stats.completedToday, 1)
                      )
                    : 0}
                </div>
                <div
                  style={{ fontSize: "12px", color: colorTheme.gray500 }}
                  className="performance-label"
                >
                  Avg Revenue/Service
                </div>
              </div>
            )}

            <div style={{ textAlign: "center" }} className="performance-metric">
              <div
                style={{
                  fontSize: "20px",
                  fontWeight: "700",
                  color: colorTheme.info,
                }}
                className="performance-value"
              >
                {stats.activePatients.toLocaleString()}
              </div>
              <div
                style={{ fontSize: "12px", color: colorTheme.gray500 }}
                className="performance-label"
              >
                Active Patients
              </div>
            </div>
            <div style={{ textAlign: "center" }} className="performance-metric">
              <div
                style={{
                  fontSize: "20px",
                  fontWeight: "700",
                  color:
                    stats.equipmentIssues > 0
                      ? colorTheme.warning
                      : colorTheme.success,
                }}
                className="performance-value"
              >
                {stats.equipmentIssues === 0 ? "✅" : stats.equipmentIssues}
              </div>
              <div
                style={{ fontSize: "12px", color: colorTheme.gray500 }}
                className="performance-label"
              >
                Equipment Issues
              </div>
            </div>
            <div style={{ textAlign: "center" }} className="performance-metric">
              <div
                style={{
                  fontSize: "20px",
                  fontWeight: "700",
                  color: colorTheme.gray600,
                }}
                className="performance-value"
              >
                {currentTime.toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                })}
              </div>
              <div
                style={{ fontSize: "12px", color: colorTheme.gray500 }}
                className="performance-label"
              >
                Current Date
              </div>
            </div>
          </div>

          {/* Role-based access notice for secretaries */}
          {userRole === "secretary" && (
            <div
              style={{
                marginTop: "24px",
                padding: "16px",
                background: `${colorTheme.info}10`,
                borderRadius: "12px",
                border: `1px solid ${colorTheme.info}30`,
                textAlign: "center",
              }}
              className="secretary-notice"
            >
              <div
                style={{
                  fontSize: "14px",
                  color: colorTheme.gray600,
                  marginBottom: "4px",
                }}
              >
                Revenue data is restricted for secretary accounts
              </div>
              <div style={{ fontSize: "12px", color: colorTheme.gray500 }}>
                Contact your administrator if you need access to financial
                information
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Enhanced Tablet-Responsive CSS */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }

          /* Tablet Styles for Dashboard (769px - 1024px) */
          @media (min-width: 769px) and (max-width: 1024px) {
            /* Dashboard header adjustments */
            .dashboard-header {
              padding: 24px 20px !important;
              margin-bottom: 20px !important;
            }

            .dashboard-title {
              font-size: 28px !important;
            }

            .dashboard-subtitle {
              font-size: 16px !important;
            }

            .dashboard-appointments-count {
              font-size: 13px !important;
            }

            .dashboard-role {
              font-size: 11px !important;
            }

            .current-time {
              font-size: 20px !important;
            }

            .time-label {
              font-size: 13px !important;
            }

            .system-status {
              font-size: 11px !important;
              padding: 6px 10px !important;
            }

            /* Metrics grid for tablet */
            .metrics-grid {
              grid-template-columns: repeat(2, 1fr) !important;
              gap: 16px !important;
              margin-bottom: 20px !important;
            }

            .metric-card {
              padding: 20px !important;
            }

            .metric-value {
              font-size: 24px !important;
            }

            .metric-title {
              font-size: 13px !important;
            }

            .metric-subtitle {
              font-size: 11px !important;
            }

            .metric-change {
              font-size: 11px !important;
              padding: 3px 6px !important;
            }

            /* Main content grid for tablet */
            .main-content-grid {
              grid-template-columns: 1fr !important;
              gap: 20px !important;
            }

            /* Schedule section adjustments */
            .schedule-container {
              margin-bottom: 20px !important;
            }

            .schedule-title {
              font-size: 18px !important;
            }

            .view-all-btn {
              padding: 6px 12px !important;
              font-size: 13px !important;
            }

            .schedule-item {
              padding: 14px !important;
              margin-bottom: 10px !important;
            }

            .patient-name {
              font-size: 15px !important;
            }

            .appointment-details {
              font-size: 13px !important;
            }

            .appointment-time {
              font-size: 13px !important;
            }

            .appointment-status {
              font-size: 11px !important;
              padding: 3px 6px !important;
            }

            /* Sidebar adjustments */
            .sidebar-container {
              gap: 16px !important;
            }

            .quick-actions-container,
            .system-alerts-container {
              padding: 18px !important;
            }

            .quick-actions-title,
            .alerts-title {
              font-size: 16px !important;
              margin-bottom: 14px !important;
            }

            .quick-actions-grid {
              gap: 10px !important;
            }

            .quick-action-btn {
              padding: 14px 10px !important;
            }

            .quick-action-icon {
              font-size: 20px !important;
              margin-bottom: 6px !important;
            }

            .quick-action-title {
              font-size: 11px !important;
            }

            .system-alert {
              padding: 10px !important;
              margin-bottom: 8px !important;
            }

            .alert-message {
              font-size: 13px !important;
            }

            .alert-timestamp {
              font-size: 11px !important;
            }

            /* Bottom section adjustments */
            .bottom-section {
              grid-template-columns: 1fr !important;
              gap: 20px !important;
            }

            .recent-activity-container,
            .revenue-overview-container {
              padding: 20px !important;
            }

            .activity-title,
            .revenue-title {
              font-size: 18px !important;
            }

            .revenue-report-btn {
              padding: 6px 12px !important;
              font-size: 13px !important;
            }

            .activity-item {
              padding: 10px 0 !important;
            }

            .activity-message {
              font-size: 13px !important;
            }

            .activity-time {
              font-size: 11px !important;
            }

            .revenue-card {
              padding: 14px !important;
              margin-bottom: 12px !important;
            }

            .revenue-value {
              font-size: 20px !important;
            }

            .revenue-subtitle {
              font-size: 11px !important;
            }

            /* Performance summary adjustments */
            .performance-summary {
              padding: 20px !important;
              margin-top: 20px !important;
            }

            .performance-title {
              font-size: 16px !important;
              margin-bottom: 14px !important;
            }

            .performance-grid {
              grid-template-columns: repeat(3, 1fr) !important;
              gap: 16px !important;
            }

            .performance-value {
              font-size: 18px !important;
            }

            .performance-label {
              font-size: 11px !important;
            }

            .secretary-notice {
              margin-top: 20px !important;
              padding: 14px !important;
            }
          }

          /* Large Tablet Optimizations (1025px - 1200px) */
          @media (min-width: 1025px) and (max-width: 1200px) {
            .metrics-grid {
              grid-template-columns: repeat(4, 1fr) !important;
              gap: 18px !important;
            }

            .main-content-grid {
              grid-template-columns: 1.8fr 1.2fr !important;
              gap: 22px !important;
            }

            .bottom-section {
              grid-template-columns: 1fr 1fr !important;
              gap: 22px !important;
            }

            .performance-grid {
              grid-template-columns: repeat(5, 1fr) !important;
            }
          }

          /* Mobile Styles (up to 768px) */
          @media (max-width: 768px) {
            .dashboard-header {
              padding: 20px 16px !important;
              margin-bottom: 16px !important;
            }

            .dashboard-title {
              font-size: 24px !important;
            }

            .dashboard-subtitle {
              font-size: 14px !important;
            }

            .header-content {
              flex-direction: column !important;
              align-items: flex-start !important;
              gap: 16px !important;
            }

            .header-time {
              text-align: left !important;
            }

            .metrics-grid,
            .main-content-grid,
            .bottom-section,
            .quick-actions-grid {
              display: flex !important;
              flex-direction: column !important;
              gap: 16px !important;
              grid-template-columns: none !important;
            }

            .metric-card,
            .schedule-container,
            .quick-actions-container,
            .system-alerts-container,
            .recent-activity-container,
            .revenue-overview-container,
            .performance-summary {
              width: 100% !important;
              margin-bottom: 16px !important;
              padding: 20px !important;
            }

            .schedule-item {
              padding: 12px !important;
            }

            .quick-action-btn {
              width: 100% !important;
              min-height: 48px !important;
              padding: 12px 16px !important;
              margin-bottom: 8px !important;
            }

            .performance-grid {
              grid-template-columns: repeat(2, 1fr) !important;
              gap: 12px !important;
            }

            .sidebar-container {
              flex-direction: column !important;
              gap: 16px !important;
            }
          }

          /* Small mobile adjustments */
          @media (max-width: 480px) {
            .dashboard-header {
              padding: 16px !important;
            }

            .dashboard-title {
              font-size: 22px !important;
            }

            .metric-card,
            .schedule-container,
            .quick-actions-container,
            .system-alerts-container,
            .recent-activity-container,
            .revenue-overview-container,
            .performance-summary {
              padding: 16px !important;
            }

            .performance-grid {
              grid-template-columns: 1fr !important;
            }
          }
        `}
      </style>
    </div>
  );
};

export default PerfectDashboard;
